import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Building2, Info, Loader2, Package, Plus, Save, Trash2 } from 'lucide-react';
import api, { errMsg } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

const slug = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || `paket-${Date.now()}`;

export const AdminSettings = () => {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings');
      setS(data);
    } catch (e) {
      toast.error(errMsg(e, 'Ayarlar yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setPkg = (i, key, val) =>
    setS((prev) => {
      const packages = [...prev.packages];
      packages[i] = { ...packages[i], [key]: val };
      return { ...prev, packages };
    });

  const addPkg = () =>
    setS((prev) => ({
      ...prev,
      packages: [
        ...prev.packages,
        { id: `paket-${prev.packages.length + 1}`, label: 'Yeni Paket', credits: 10, price: 0, desc: '', popular: false },
      ],
    }));

  const delPkg = (i) =>
    setS((prev) => ({ ...prev, packages: prev.packages.filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        packages: s.packages.map((p) => ({
          id: slug(p.id || p.label),
          label: p.label || 'Paket',
          credits: Number(p.credits) || 0,
          price: Number(p.price) || 0,
          desc: p.desc || '',
          popular: !!p.popular,
        })),
        bank: s.bank,
        contact: s.contact,
        payment_note: s.payment_note || '',
        bank_enabled: !!s.bank_enabled,
      };
      const { data } = await api.put('/admin/settings', payload);
      setS(data);
      toast.success('Ayarlar kaydedildi');
    } catch (e) {
      toast.error(errMsg(e, 'Kaydedilemedi'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !s) {
    return (
      <div className="grid place-items-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-red))]" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2.5 rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] p-3.5">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand-red))]" />
        <p className="text-xs leading-relaxed text-[hsl(var(--text-muted))]">
          Aşağıya IBAN bilgilerinizi girin. Kullanıcı havale/EFT ile öder, sonra &quot;Ödemeyi Yaptım&quot; formunu doldurur. Siz Ödeme Talepleri sekmesinden onaylayınca kapak hakkı otomatik yüklenir.
          Alternatif olarak Hak Kodları sekmesinden kod üretip müşteriye verebilirsiniz.
        </p>
      </div>

      {/* Packages */}
      <section className="panel p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-[hsl(var(--brand-red))]" />
            <h2 className="font-display text-sm font-semibold">Kapak Hakkı Paketleri</h2>
          </div>
          <Button
            size="sm"
            variant="secondary"
            data-testid="settings-add-package"
            onClick={addPkg}
            className="h-8 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] text-xs"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Paket Ekle
          </Button>
        </div>

        <div className="mt-4 space-y-4">
          {s.packages.map((p, i) => (
            <div
              key={`${p.id}-${i}`}
              data-testid={`settings-package-${i}`}
              className="rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] p-4"
            >
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Paket adı</Label>
                  <Input
                    data-testid={`settings-package-label-${i}`}
                    value={p.label}
                    onChange={(e) => setPkg(i, 'label', e.target.value)}
                    className="bg-[hsl(var(--surface))]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Hak</Label>
                  <Input
                    type="number"
                    data-testid={`settings-package-credits-${i}`}
                    value={p.credits}
                    onChange={(e) => setPkg(i, 'credits', e.target.value)}
                    className="bg-[hsl(var(--surface))] font-mono-alt"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Fiyat (TL)</Label>
                  <Input
                    type="number"
                    data-testid={`settings-package-price-${i}`}
                    value={p.price}
                    onChange={(e) => setPkg(i, 'price', e.target.value)}
                    className="bg-[hsl(var(--surface))] font-mono-alt"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Açıklama</Label>
                  <Input
                    data-testid={`settings-package-desc-${i}`}
                    value={p.desc}
                    onChange={(e) => setPkg(i, 'desc', e.target.value)}
                    className="bg-[hsl(var(--surface))]"
                  />
                </div>
              </div>


              <div className="mt-3 flex items-center gap-4">
                <label className="flex cursor-pointer items-center gap-2 text-xs">
                  <Switch
                    data-testid={`settings-package-popular-${i}`}
                    checked={!!p.popular}
                    onCheckedChange={(v) => setPkg(i, 'popular', v)}
                  />
                  Popüler olarak işaretle
                </label>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`settings-package-delete-${i}`}
                  onClick={() => delPkg(i)}
                  className="ml-auto h-8 text-xs text-[hsl(var(--text-muted))] hover:text-[hsl(var(--brand-red))]"
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Sil
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-5">
          <label className="flex cursor-pointer items-center gap-2 text-xs">
            <Switch
              data-testid="settings-bank-enabled"
              checked={!!s.bank_enabled}
              onCheckedChange={(v) => setS({ ...s, bank_enabled: v })}
            />
            IBAN / havale seçeneği aktif
          </label>
        </div>
      </section>

      {/* Bank */}
      <section className="panel p-5">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-[hsl(var(--brand-red))]" />
          <h2 className="font-display text-sm font-semibold">Banka / IBAN Bilgileri</h2>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Alıcı adı</Label>
            <Input
              data-testid="settings-bank-name-input"
              value={s.bank?.account_name || ''}
              onChange={(e) => setS({ ...s, bank: { ...s.bank, account_name: e.target.value } })}
              className="bg-[hsl(var(--surface-2))]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Banka</Label>
            <Input
              data-testid="settings-bank-bankname-input"
              value={s.bank?.bank_name || ''}
              onChange={(e) => setS({ ...s, bank: { ...s.bank, bank_name: e.target.value } })}
              className="bg-[hsl(var(--surface-2))]"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">IBAN</Label>
            <Input
              data-testid="settings-bank-iban-input"
              value={s.bank?.iban || ''}
              onChange={(e) => setS({ ...s, bank: { ...s.bank, iban: e.target.value } })}
              placeholder="TR00 0000 0000 0000 0000 0000 00"
              className="bg-[hsl(var(--surface-2))] font-mono-alt"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Havale notu (kullanıcıya gösterilir)</Label>
            <Input
              data-testid="settings-bank-note-input"
              value={s.bank?.note || ''}
              onChange={(e) => setS({ ...s, bank: { ...s.bank, note: e.target.value } })}
              placeholder="Açıklamaya e-posta adresini yaz"
              className="bg-[hsl(var(--surface-2))]"
            />
          </div>
        </div>
      </section>

      {/* Note + contact */}
      <section className="panel p-5">
        <h2 className="font-display text-sm font-semibold">Ödeme Açıklaması & İletişim</h2>
        <div className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Hesabım sayfasında görünecek açıklama</Label>
            <Textarea
              rows={3}
              data-testid="settings-payment-note-input"
              value={s.payment_note || ''}
              onChange={(e) => setS({ ...s, payment_note: e.target.value })}
              className="resize-y bg-[hsl(var(--surface-2))]"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Destek e-posta</Label>
              <Input
                data-testid="settings-contact-email-input"
                value={s.contact?.email || ''}
                onChange={(e) => setS({ ...s, contact: { ...s.contact, email: e.target.value } })}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">WhatsApp</Label>
              <Input
                data-testid="settings-contact-whatsapp-input"
                value={s.contact?.whatsapp || ''}
                onChange={(e) => setS({ ...s, contact: { ...s.contact, whatsapp: e.target.value } })}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Instagram</Label>
              <Input
                data-testid="settings-contact-instagram-input"
                value={s.contact?.instagram || ''}
                onChange={(e) => setS({ ...s, contact: { ...s.contact, instagram: e.target.value } })}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
          </div>
        </div>
      </section>

      <Button
        onClick={save}
        disabled={saving}
        data-testid="settings-save-button"
        className="h-11 w-full bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))] sm:w-auto sm:px-8"
      >
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        Ayarları Kaydet
      </Button>
    </div>
  );
};
